'use client'
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/shadcn'
import { cn } from '@/shared/lib/classnames/cn'
import { useIsMobile } from '@/shared/utils'
import { isToday } from 'date-fns'
import { ru } from 'date-fns/locale'
import { CalendarIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

interface DatePickerProps {
  className?: string
  variant?: 'default' | 'outline'
  size?: 'default' | 'lg'
  onDateChange?: (dateUtc: Date, type: 'day' | 'month') => void
  defaultValue?: Date
  placeholder?: string
  iconPosition?: 'left' | 'rigth'
  error?: string
  monthButton?: boolean
  value?: Date
}

export const DatePicker = ({
  className,
  size = 'default',
  variant = 'default',
  onDateChange,
  defaultValue,
  placeholder,
  error,
  monthButton = true,
  value,
}: DatePickerProps) => {
  const isMobile = useIsMobile()
  const [internalDate, setInternalDate] = useState<Date | undefined>(defaultValue)
  const date = value !== undefined ? value : internalDate
  const [calendarMonth, setCalendarMonth] = useState<Date>(defaultValue ?? new Date())
  const [open, setOpen] = useState(false)

  const [isMonthSelected, setIsMonthSelected] = useState(false)

  useEffect(() => {
    setInternalDate(value)
  }, [value])

  const handleDaySelect = (selectedDate: Date | undefined) => {
    if (!selectedDate || error) return

    const utcDate = new Date(
      Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate())
    )

    setInternalDate(selectedDate)
    setIsMonthSelected(false)
    setOpen(false)
    onDateChange?.(utcDate, 'day')
  }

  const handlePickMonth = () => {
    if (error) return

    const firstDay = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1)
    const utcDate = new Date(Date.UTC(firstDay.getFullYear(), firstDay.getMonth(), 1))

    setInternalDate(firstDay)
    setIsMonthSelected(true)
    setOpen(false)
    onDateChange?.(utcDate, 'month')
  }

  const getDisplayLabel = () => {
    if (!date) return placeholder ?? 'Выберите дату'

    if (isMonthSelected) {
      return date.toLocaleDateString('ru-RU', {
        month: 'long',
        year: 'numeric',
      })
    }

    if (isToday(date)) return 'Сегодня'

    return isMobile
      ? date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'numeric',
          year: 'numeric',
        })
      : date.toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
  }

  return (
    <div className="">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            size={size}
            id="date"
            variant={variant}
            className={cn(
              !date && 'text-muted-foreground',
              className,
              'text-xs md:text-base w-full md:justify-between'
            )}
          >
            <CalendarIcon className="!w-5 !h-5 text-primary dark:text-primary-dark" />
            <span>{getDisplayLabel()}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-auto" align="end">
          {monthButton && (
            <div className="p-3 border-b">
              <Button onClick={handlePickMonth} className="w-full">
                Выбрать месяц
              </Button>
            </div>
          )}

          <Calendar
            initialFocus
            mode="single"
            selected={!isMonthSelected ? date : undefined}
            onSelect={handleDaySelect}
            onMonthChange={setCalendarMonth}
            defaultMonth={calendarMonth}
            numberOfMonths={1}
            locale={ru}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
